import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/authApi';
import { useAuthStore } from '@/state/authStore';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { FundSelector } from '@/components/forms/FundSelector';
import { DatePicker } from '@/components/forms/DatePicker';
import { AdminOnly } from '@/components/admin/AdminOnly';
import { dashboardsApi } from '@/api/dashboardsApi';
import type { DashboardSummary } from '@/api/schemas';
import css from './Sidebar.module.css';

const DASH_STALE = 5 * 60 * 1000;

function DashboardsSidebarSection({
  isAdmin,
  selectedId,
}: {
  isAdmin: boolean;
  selectedId: number | null;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');

  const listQuery = useQuery({
    queryKey: ['dashboards'],
    queryFn: () => dashboardsApi.list(),
    staleTime: DASH_STALE,
  });

  const createDashboard = useMutation({
    mutationFn: () => dashboardsApi.create(newName.trim()),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['dashboards'] });
      navigate(`/dashboards?id=${d.id}`);
      setNewName('');
      setShowNewForm(false);
      setCreateError('');
    },
    onError: (e: unknown) =>
      setCreateError(e instanceof Error ? e.message : 'Create failed'),
  });

  const dashboards: DashboardSummary[] = (listQuery.data?.dashboards ?? []).filter(
    (d) => isAdmin || d.is_published,
  );

  return (
    <div className={css.dashSection}>
      <div className={css.dashHeader}>
        <span className={css.dashTitle}>Dashboards</span>
        {isAdmin && (
          <button
            className={css.dashNewBtn}
            onClick={() => setShowNewForm((v) => !v)}
            title="New dashboard"
          >
            +
          </button>
        )}
      </div>

      {isAdmin && showNewForm && (
        <div className={css.dashNewForm}>
          <input
            className={css.dashFormInput}
            placeholder="Dashboard name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) createDashboard.mutate();
            }}
            autoFocus
          />
          {createError && <p className={css.dashFormError}>{createError}</p>}
          <div className={css.dashFormActions}>
            <button
              className={css.dashSaveBtn}
              onClick={() => createDashboard.mutate()}
              disabled={!newName.trim() || createDashboard.isPending}
            >
              Create
            </button>
            <button className={css.dashCancelBtn} onClick={() => setShowNewForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <nav className={css.dashList} aria-label="Dashboard list">
        {dashboards.map((d) => (
          <button
            key={d.id}
            className={`${css.dashItem} ${selectedId === d.id ? css.dashItemActive : ''}`}
            onClick={() => navigate(`/dashboards?id=${d.id}`)}
          >
            <span className={css.dashItemName}>{d.name}</span>
            {isAdmin && !d.is_published && (
              <span className={css.dashDraftBadge}>Draft</span>
            )}
          </button>
        ))}
        {dashboards.length === 0 && !listQuery.isLoading && (
          <p className={css.dashEmpty}>
            {isAdmin ? 'No dashboards yet.' : 'No published dashboards.'}
          </p>
        )}
      </nav>
    </div>
  );
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const qc = useQueryClient();
  const location = useLocation();

  const onDashboards = location.pathname.startsWith('/dashboards');
  const selectedId = onDashboards
    ? (() => {
        const id = new URLSearchParams(location.search).get('id');
        return id ? Number(id) : null;
      })()
    : null;

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setUser(null);
      qc.clear();
      window.location.href = '/login';
    },
  });

  return (
    <aside className={css.sidebar} aria-label="Primary navigation">
      <div className={css.brand}>
        <img src="/arp-logo.png" alt="ARP" className={css.logo} />
        <div className={css.brandText}>
          <div className={css.brandTitle}>ARP Analytics</div>
          {user && (
            <div className={css.brandUser}>
              {user.name}{isAdmin && <span className={css.adminBadge}>Admin</span>}
            </div>
          )}
        </div>
      </div>

      <nav className={css.nav} aria-label="Pages">
        <NavLink to="/attribution/summary" className={({ isActive }) =>
          `${css.navLink} ${isActive ? css.navLinkActive : ''}`}>
          🎯 Attribution Reports
        </NavLink>
        <NavLink to="/dashboards" className={({ isActive }) =>
          `${css.navLink} ${isActive ? css.navLinkActive : ''}`}>
          📊 Dashboards
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) =>
          `${css.navLink} ${isActive ? css.navLinkActive : ''}`}>
          ⚙️ Settings
        </NavLink>
        <AdminOnly>
          <NavLink to="/admin/users" className={({ isActive }) =>
            `${css.navLink} ${isActive ? css.navLinkActive : ''}`}>
            👥 Users
          </NavLink>
        </AdminOnly>
      </nav>

      {onDashboards && (
        <DashboardsSidebarSection isAdmin={isAdmin} selectedId={selectedId} />
      )}

      <div className={css.section}>
        <FundSelector />
      </div>
      <div className={css.section}>
        <DatePicker />
      </div>

      <div className={css.footer}>
        <ThemeToggle />
        <button
          className={css.logoutButton}
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
        >
          {logout.isPending ? 'Logging out…' : 'Logout'}
        </button>
      </div>
    </aside>
  );
}
